import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, Download, FileText } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as reportsApi from '../../api/reports.api';
import { errorMessage } from '../../api/client';
import { formatDate, fileSize } from '../../utils/format';

function Reports() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdminish = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = isAdminish ? await reportsApi.listAdminReports() : await reportsApi.myReports();
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isAdminish]);

  async function onDownload(r) {
    try {
      await reportsApi.downloadReportFile(r._id, r.fileName);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <>
      <PageHeader
        title={isAdminish ? 'User progress' : 'My progress'}
        subtitle={isAdminish ? 'Weekly weigh-ins, photos, and lab results uploaded by clinics.' : 'Weigh-ins, photos, and lab results uploaded by your clinic.'}
      />

      <DataTable
        loading={loading}
        rows={rows}
        searchPlaceholder="Search by file name, plan, clinic…"
        columns={[
          {
            key: 'file',
            header: 'Report',
            render: (r) => (
              <div className="row">
                <div className="stat-icon brand" style={{ width: 36, height: 36, background: 'var(--brand-50)', color: 'var(--brand-700)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={16} />
                </div>
                <div>
                  <div className="cell-strong">{r.fileName}</div>
                  <div className="muted text-xs">{r.fileType} · {fileSize(r.fileSize)}</div>
                </div>
              </div>
            )
          },
          {
            key: 'package',
            header: 'Package',
            render: (r) => <span>{r.package?.name || '—'}</span>
          },
          ...(isAdminish ? [{
            key: 'patient',
            header: 'User',
            render: (r) => <span>{r.user?.name || r.user?.email || '—'}</span>
          }] : []),
          {
            key: 'vendor',
            header: 'Lab',
            render: (r) => <span>{r.vendor?.name || '—'}</span>
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status} />
          },
          {
            key: 'uploadedAt',
            header: 'Uploaded',
            render: (r) => <span className="muted">{formatDate(r.uploadedAt)}</span>
          },
          {
            key: 'actions',
            header: '',
            width: 120,
            render: (r) => (
              <div className="row-actions">
                <Link to={`/reports/${r._id}`} className="icon-btn" title="View"><Eye size={16} /></Link>
                <button className="icon-btn" title="Download" onClick={() => onDownload(r)}>
                  <Download size={16} />
                </button>
              </div>
            )
          }
        ]}
        emptyTitle="No reports yet"
        emptyMessage={isAdminish ? 'Reports will appear here once vendors upload them.' : 'When your lab uploads your report, it will appear here.'}
      />
      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}
    </>
  );
}

export default Reports;
